import React from 'react'
import { IoHomeOutline, IoHammerOutline, IoTrendingUpOutline, IoPulseOutline, IoFilterOutline } from 'react-icons/io5'
import { useBetBuilder } from '../context/BetContext'
import { Nav } from './Layout'

export type PageId = 'dashboard' | 'fixtures' | 'mybets' | 'roadmap' | 'valuebets' | 'livetips' | 'admin' | 'referees' | 'aipredictions' | 'cardshub' | 'goalshub' | 'playerstats' | 'strategies' | 'dixoncoles' | 'match' | 'league'

interface NavItem {
  id: string
  label: string
  pageId?: PageId
  icon: React.ComponentType<{ size?: number; color?: string }>
}

// Reduced to 5 items for optimal mobile UX
// Dashboard as home, key pages, and builder modal
const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Home', pageId: 'dashboard', icon: IoHomeOutline },
  { id: 'valuebets', label: 'Value', pageId: 'valuebets', icon: IoTrendingUpOutline },
  { id: 'dixoncoles', label: 'DC Data', pageId: 'dixoncoles', icon: IoPulseOutline },
  { id: 'strategies', label: 'Strategies', pageId: 'strategies', icon: IoFilterOutline },
  { id: 'builder', label: 'Builder', icon: IoHammerOutline },
]

interface BottomNavProps {
  activePage: PageId
  onPageChange: (page: PageId) => void
}

export function BottomNav({ activePage, onPageChange }: BottomNavProps) {
  const { bets, openModal } = useBetBuilder()

  const handleClick = (item: NavItem) => {
    if (item.id === 'builder') {
      openModal()
    } else if (item.pageId) {
      onPageChange(item.pageId)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, item: NavItem) => {
    // Allow activation with Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick(item)
    }
  }

  // Determine if this item is the current page
  const isActive = (item: NavItem) => {
    if (item.id === 'builder') return false // Builder opens modal, never "active"
    return item.pageId === activePage
  }

  // Build accessible label with badge info
  const getAccessibleLabel = (item: NavItem) => {
    let label = item.label
    if (item.id === 'builder' && bets.length > 0) {
      label += `, ${bets.length} item${bets.length !== 1 ? 's' : ''} in builder`
    }
    return label
  }

  return (
    <Nav aria-label="Main navigation" className="footer-nav">
      <div className="footer-inner" role="tablist" aria-label="App sections">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item)
          const accessibleLabel = getAccessibleLabel(item)

          return (
            <button
              key={item.id}
              role="tab"
              aria-selected={active}
              aria-current={active ? 'page' : undefined}
              aria-label={accessibleLabel}
              className={`navbtn ${active ? 'active' : ''}`}
              onClick={() => handleClick(item)}
              onKeyDown={(e) => handleKeyDown(e, item)}
              type="button"
            >
              {/* Badge for builder items count */}
              {item.id === 'builder' && bets.length > 0 && (
                <span className="nav-badge" aria-hidden="true">
                  {bets.length}
                </span>
              )}
              <item.icon size={22} aria-hidden="true" />
              <span className="navbtn-label">{item.label}</span>
            </button>
          )
        })}
      </div>
    </Nav>
  )
}
